import { useLingui } from "@lingui/react/macro";
import { formatCurrency } from "@offstreetparking/design-system/formatters";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import type { PaymentScheduleSummary } from "@offstreetparking/domain/value/payment-schedule";
import { getCountryCurrency } from "@offstreetparking/geography";
import { FiniteSummaryRows } from "./components/FiniteSummaryRows";
import { OngoingSummaryRows } from "./components/OngoingSummaryRows";

type PaymentScheduleOverviewProps = {
	overview: PaymentScheduleSummary;
	country?: string;
};

export const PaymentScheduleOverview = ({ overview, country }: PaymentScheduleOverviewProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	const currency = getCountryCurrency(country ?? undefined);
	const { remainderDeductions } = overview;
	const oneCent = formatCurrency({ locale, valueInCents: 1, currency });

	return (
		<div>
			<section
				aria-label={t`Payroll deduction summary`}
				className="overflow-hidden rounded-lg border border-neutral"
			>
				<div className="border-neutral border-b bg-neutral-faded px-4 py-2.5">
					<Text size="sm" weight="bold" As="span">
						{t`Payroll deduction summary`}
					</Text>
				</div>
				<div className="space-y-3 p-4">
					{overview.isOngoing ? (
						<OngoingSummaryRows overview={overview} locale={locale} currency={currency} />
					) : (
						<FiniteSummaryRows overview={overview} locale={locale} currency={currency} />
					)}
				</div>
			</section>
			{remainderDeductions && (
				<Text size="sm" intent="muted" className="mt-2">
					{t`Deductions may vary by ${oneCent} due to rounding.`}
				</Text>
			)}
		</div>
	);
};
